import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, List, ListItem, Typography } from '@mui/material';

import Modal from '../../components/UI/Popup/BackdropComponent';
import useTranslate from '../../hooks/useTranslate';
import useDetectResize from '../../hooks/useDetectResize';
import { NavTitle } from '../UI/StyledComponents/NavigationStyledComponents';
import {
  CenteredFullBackDrop,
  CustomNavDrawer,
  InlineContainer,
} from '../../styled-components/GlobalStyledComponents';
import { CenteredFullHelpOverlay } from '../UI/StyledComponents/NotificationModalStyledComponents';
import CloseButton from '../SharedComponents/CloseButton';
import LoadHelpContent from './LoadHelpContent';

import Icon from '../../assets/icons/icons';
import miniLogo from '../../assets/images/new-logo-v.svg';
import Logo from '../../assets/images/new-logo-lrg.svg';

const navIconStyling = {
  justifyContent: 'center !important',
  width: '50px !important',
  marginLeft: 'auto !important',
  marginRight: 'auto !important',
};

const NavigationComponent = (props: {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isCollapsed, setIsCollapsed } = props;
  const { browserWindow } = useDetectResize();
  let activeNavBtn = 'active-nav inline';
  let navBtn = 'inactive-nav inline';
  const location = useLocation(); //use this to toggle images when user navigates to different sections of the site.
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const t = useTranslate();

  const toggleNav = () => {
    setIsCollapsed((currState) => !currState);
  };

  return (
    //Add sx object which detects width and media type.
    //
    <CustomNavDrawer
      className="nav-bar"
      variant="permanent"
      anchor="left"
      sx={{ zIndex: 0 }}
      isCollapsed={isCollapsed}
    >
      <Box
        sx={{
          height: '90px',
        }}
      >
        {isCollapsed ? (
          <Box
            className="platform-logo nav-mini-logo "
            onClick={() => {
              toggleNav();
            }}
          >
            <img className={`dash-sm-logo`} src={miniLogo} alt="small-logo" loading="lazy" />
          </Box>
        ) : (
          <Box className="platform-logo">
            <img
              className="dash-lg-logo"
              src={Logo}
              alt="MyJobMatch Logo with a tagline that says 'Building Connections for an Inclusive Future"
              loading="lazy"
              onClick={() => {
                toggleNav();
              }}
            />
          </Box>
        )}
      </Box>

      <List
        className="nav-top-list"
        sx={{
          paddingTop: '1.4rem',
          flex: 0.98,
          '@media (max-resolution: 1dppx) and (min-height: 1030px)': {
            paddingTop: '1.9rem',
          },
        }}
      >
        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/dashboard"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={location.pathname === '/dashboard' ? Icon.Dashboard : Icon.Dashboard}
                alt="dash"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('dashboardNav')}
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>

        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/profile"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={
                  location.pathname.startsWith('/profile') ||
                  location.pathname.startsWith('/Profile')
                    ? Icon.Profile
                    : Icon.Profile
                }
                alt="profile"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('mobileNav-myProfile')}
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>

        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/jobs"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={
                  location.pathname.startsWith('/jobs') || location.pathname.startsWith('/Jobs')
                    ? Icon.Jobs
                    : Icon.Jobs
                }
                alt="jobs"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('jobNav')}
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>

        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/myjobmatches"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={
                  location.pathname.startsWith('/myjobmatches') ||
                  location.pathname.startsWith('/Myjobmatches')
                    ? Icon.MyJobMatches
                    : Icon.MyJobMatches
                }
                alt="myjobmatches"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('mobileNav-myJobMatches')}
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>

        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/activity"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={
                  location.pathname.startsWith('/activity') ||
                  location.pathname.startsWith('/Activity')
                    ? Icon.MyActivity
                    : Icon.MyActivity
                }
                alt="myactivity"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('mobileNav-myActivity')}
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>

        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <NavLink
            to="/chat"
            className={(props: { isActive: boolean }) => (props.isActive ? activeNavBtn : navBtn)}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
            >
              <img
                className="nav-icon"
                src={
                  location.pathname.startsWith('/chat') ||
                  location.pathname.startsWith('/Chat')
                    ? Icon.ChatIcon
                    : Icon.ChatIcon
                }
                alt="myactivity"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  Chat
                </NavTitle>
              )}
            </InlineContainer>
          </NavLink>
        </ListItem>
      </List>

      <List sx={{ paddingTop: '1.4em' }}>
        <ListItem sx={isCollapsed ? navIconStyling : {}}>
          <Box
            className="inactive-nav"
            onClick={() => {
              setShowHelpModal(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setShowHelpModal(true);
              }
            }}
            tabIndex={0}
          >
            <InlineContainer
              justifyContent="flex-start"
              className={`${!isCollapsed && 'nav-txt-wrpr'}`}
              sx={{
                cursor: 'pointer',
              }}
            >
              <img
                className="nav-icon"
                src={location.pathname === '/nothing-here-yet' ? Icon.Help : Icon.Help}
                alt="dash"
                loading="lazy"
              />
              {!isCollapsed && (
                <NavTitle variant="h3" className={!isCollapsed && 'animate-rotate-y'}>
                  {t('helpBtn')}
                </NavTitle>
              )}
            </InlineContainer>
          </Box>
        </ListItem>
      </List>
      {showHelpModal && (
        <Modal className="help-popup" onHide={() => {}}>
          <CenteredFullHelpOverlay
            className={location.pathname.slice(1)}
            top={browserWindow.h}
            right={browserWindow.w}
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              e.stopPropagation();
            }}
          >
            <CenteredFullBackDrop>
              <InlineContainer
                justifyContent="space-between"
                alignItems="flex-end"
                sx={{
                  '& > div.close-button': {
                    marginBottom: '20px',
                  },
                }}
              >
                <Typography variant="h1" paddingLeft="0.5em">
                  {t('help')}
                </Typography>
                <CloseButton
                  onHide={() => {
                    setShowHelpModal(false);
                  }}
                />
              </InlineContainer>
              <Box>
                <LoadHelpContent />
              </Box>
            </CenteredFullBackDrop>
          </CenteredFullHelpOverlay>
        </Modal>
      )}
    </CustomNavDrawer>
  );
};

export default NavigationComponent;
